/**
 * @module useZoomWarningPopup
 * @description A custom React hook that manages zoom level warnings for the application
 * @returns {Object} An empty object
 */

import { useEffect } from 'react';
import { closeLayoutAlertOf } from '../components';
import { getZoomLevel, isMobileDevice } from '../utils/common';
import { showLayoutAlertWarning } from '../components/LayoutAlert/alert';

/**
 * @constant {string} LOCAL_STORAGE_KEY Key used to store zoom warning dismissed state in session storage
 */
const LOCAL_STORAGE_KEY = 'zoomWarningDismissed';

/**
 * @function useZoomWarningPopup
 * @description Custom hook that monitors browser zoom level and shows warning when zoom exceeds 90%
 * @returns {Object} Empty object
 */
const useZoomWarningPopup = () => {
  /**
   * Effect hook to check zoom status on mount and handle window resize events
   * Monitors browser zoom level and displays warning alert if:
   * - Zoom level is > 90%
   * - Not on a mobile device
   * - Warning hasn't been dismissed
   * Cleans up event listeners and alerts on unmount
   */
  useEffect(() => {
    let currentAlert = null;
    /**
     * @function checkZoomStatus
     * @description Checks current zoom level and shows/hides warning alert accordingly
     */
    const checkZoomStatus = () => {
      const zoomLevel = getZoomLevel();
      const isMobile = isMobileDevice();
      const dismissed = localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
      if (currentAlert) {
        closeLayoutAlertOf(currentAlert);
      }

      if (zoomLevel > 0.9 && !isMobile && !dismissed) {
        const alert = showLayoutAlertWarning(
          'Optimize Your Experience',
          `For the best experience, we recommend setting your browser zoom level to 90% or lower. High zoom levels may affect layout and usability.`,
          () => localStorage.setItem(LOCAL_STORAGE_KEY, 'true')
        );
        currentAlert = alert;
      } else {
        currentAlert = null;
      }
    };
    checkZoomStatus();
    window.addEventListener('resize', checkZoomStatus);
    return () => {
      if (currentAlert) {
        closeLayoutAlertOf(currentAlert);
      }
      window.removeEventListener('resize', checkZoomStatus);
    };
  }, []);

  return {};
};

export default useZoomWarningPopup;
